<template>
  <v-dialog
    v-model="showModal"
    max-width="600"
    persistent
    transition="scale-transition"
  >
    <v-card class="save-auth-modal" elevation="8">
      <v-card-title class="modal-header pa-6">
        <v-icon size="32" color="primary" class="mr-3">
          {{ getCurrentStepIcon }}
        </v-icon>
        <span class="text-h5">{{ getCurrentStepTitle }}</span>
      </v-card-title>

      <v-card-text class="pa-6">
        <div class="modal-content">
          <!-- Sign In / Sign Up Tab Selection -->
          <v-expand-transition>
            <div v-if="currentStep === 'auth'">
              <p class="text-body-1 mb-4">
                To save your memory progress, please sign in or create an account.
              </p>

              <v-alert v-if="error" type="error" variant="tonal" class="mb-4">
                {{ error }}
              </v-alert>
              <div v-if="currentTab === 'signup' && showSignupResetHint" class="text-center mb-4">
                <v-btn
                  variant="text"
                  color="primary"
                  size="small"
                  @click="showForgotPassword = true"
                >
                  Forgot your password?
                </v-btn>
              </div>

              <v-tabs v-model="currentTab" class="mb-4 auth-tabs" color="primary">
                <v-tab value="signup">Create Account</v-tab>
                <v-tab value="signin">Sign In</v-tab>
              </v-tabs>

              <v-window v-model="currentTab">
                <!-- Sign In Tab -->
                <v-window-item value="signin">
                  <v-form ref="signinForm" v-model="signinValid" @submit.prevent="handleSignIn">
                    <v-text-field
                      v-model="signinEmail"
                      label="Email address"
                      type="email"
                      variant="outlined"
                      density="comfortable"
                      :rules="emailRules"
                      required
                      class="mb-3"
                    ></v-text-field>

                    <v-text-field
                      v-model="signinPassword"
                      label="Password"
                      :type="showSigninPassword ? 'text' : 'password'"
                      variant="outlined"
                      density="comfortable"
                      :rules="passwordRules"
                      required
                      :append-inner-icon="showSigninPassword ? 'mdi-eye' : 'mdi-eye-off'"
                      @click:append-inner="showSigninPassword = !showSigninPassword"
                      class="mb-3"
                    ></v-text-field>

                    <div class="text-center">
                      <v-btn
                        variant="text"
                        color="primary"
                        size="small"
                        @click="showForgotPassword = true"
                      >
                        Forgot your password?
                      </v-btn>
                    </div>
                  </v-form>
                </v-window-item>

                <!-- Sign Up Tab -->
                <v-window-item value="signup">
                  <v-form ref="signupForm" v-model="signupValid" @submit.prevent="handleSignUp">
                    <v-text-field
                      v-model="signupName"
                      label="Name"
                      variant="outlined"
                      density="comfortable"
                      :rules="nameRules"
                      required
                      class="mb-1"
                    ></v-text-field>
                    <p class="text-caption text--secondary name-note mb-4">
                      Add a name so your public contributions show your name instead of your email.
                    </p>

                    <v-text-field
                      v-model="signupEmail"
                      label="Email address"
                      type="email"
                      variant="outlined"
                      density="comfortable"
                      :rules="emailRules"
                      required
                      class="mb-3"
                    ></v-text-field>

                    <v-text-field
                      v-model="signupPassword"
                      label="Password"
                      :type="showSignupPassword ? 'text' : 'password'"
                      variant="outlined"
                      density="comfortable"
                      :rules="passwordRules"
                      required
                      :append-inner-icon="showSignupPassword ? 'mdi-eye' : 'mdi-eye-off'"
                      @click:append-inner="showSignupPassword = !showSignupPassword"
                      class="mb-3"
                    ></v-text-field>

                    <v-text-field
                      v-model="signupPasswordConfirm"
                      label="Confirm password"
                      :type="showSignupPasswordConfirm ? 'text' : 'password'"
                      variant="outlined"
                      density="comfortable"
                      :rules="confirmPasswordRules"
                      required
                      :append-inner-icon="showSignupPasswordConfirm ? 'mdi-eye' : 'mdi-eye-off'"
                      @click:append-inner="showSignupPasswordConfirm = !showSignupPasswordConfirm"
                      class="mb-3"
                    ></v-text-field>
                  </v-form>
                </v-window-item>
              </v-window>
            </div>
          </v-expand-transition>

          <!-- Verification Step -->
          <v-expand-transition>
            <div v-if="currentStep === 'verify'">
              <div class="mb-4">
                <p class="text-body-1 mb-2">
                  We've sent a verification code to:
                </p>
                <p class="text-h6 primary--text">{{ signupEmail }}</p>
              </div>

              <v-alert v-if="error" type="error" variant="tonal" class="mb-4">
                <div class="d-flex align-center justify-space-between">
                  <span>{{ error }}</span>
                  <v-btn
                    v-if="canRetryError"
                    color="error"
                    variant="text"
                    size="small"
                    @click="retryLastAction"
                    :disabled="loading"
                  >
                    Try Again
                  </v-btn>
                </div>
              </v-alert>

              <v-alert
                v-if="loading"
                type="info"
                variant="tonal"
                density="compact"
                class="mb-4"
              >
                <div class="d-flex align-center">
                  <v-progress-circular
                    indeterminate
                    size="20"
                    width="2"
                    color="primary"
                    class="mr-3"
                  ></v-progress-circular>
                  Verifying your email...
                </div>
              </v-alert>

              <v-form ref="verifyForm" v-model="verifyValid">
                <v-text-field
                  v-model="verificationCode"
                  label="Enter verification code"
                  variant="outlined"
                  density="comfortable"
                  :rules="codeRules"
                  required
                  autofocus
                  maxlength="6"
                  :disabled="loading"
                ></v-text-field>
              </v-form>

              <div class="verification-actions mt-4">
                <v-btn
                  variant="text"
                  color="secondary"
                  @click="resendCode"
                  :disabled="resendCooldown > 0 || loading"
                  :loading="resending"
                  size="small"
                >
                  {{ resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend Code' }}
                </v-btn>

                <v-btn
                  variant="text"
                  color="secondary"
                  @click="goBackToSignup"
                  size="small"
                  class="ml-4"
                >
                  Change Email
                </v-btn>
              </div>
            </div>
          </v-expand-transition>
        </div>
      </v-card-text>

      <v-card-actions class="pa-6 pt-0">
        <v-btn
          variant="outlined"
          color="primary"
          @click="handleCancel"
          class="mr-3"
          size="large"
          :disabled="loading"
        >
          <v-icon left>mdi-close</v-icon>
          Cancel
        </v-btn>

        <v-spacer></v-spacer>

        <v-btn
          v-if="currentStep === 'auth'"
          color="primary"
          @click="handleSubmit"
          size="large"
          elevation="0"
          :loading="loading"
          :disabled="!isFormValid || loading"
        >
          <v-icon left>{{ currentTab === 'signin' ? 'mdi-login' : 'mdi-account-plus' }}</v-icon>
          {{ currentTab === 'signin' ? 'Sign In & Save' : 'Create Account' }}
        </v-btn>

        <v-btn
          v-if="currentStep === 'verify'"
          color="primary"
          @click="verifyCode"
          size="large"
          elevation="0"
          :loading="loading"
          :disabled="!verifyValid || loading"
        >
          <v-icon left>mdi-check-circle</v-icon>
          Verify & Save
        </v-btn>
      </v-card-actions>
    </v-card>

    <!-- Forgot Password Inner Dialog -->
    <v-dialog v-model="showForgotPassword" max-width="450" persistent>
      <v-card class="forgot-password-card">
        <v-card-title class="text-h6 pa-4">
          <v-icon color="primary" size="small" class="mr-2">mdi-lock-reset</v-icon>
          Reset Password
        </v-card-title>

        <v-card-text class="pa-4 pt-0">
          <template v-if="!resetEmailSent">
            <p class="text-body-2 mb-3">
              Enter your email address and we'll send you a link to reset your password.
            </p>

            <v-alert v-if="resetError" type="error" variant="tonal" density="compact" class="mb-3">
              {{ resetError }}
            </v-alert>

            <v-form ref="resetForm" v-model="resetFormValid" @submit.prevent="handleSendResetEmail">
              <v-text-field
                v-model="resetEmail"
                label="Email address"
                type="email"
                variant="outlined"
                density="compact"
                :rules="emailRules"
                required
              ></v-text-field>
            </v-form>
          </template>

          <template v-else>
            <v-alert type="success" variant="tonal" density="compact" class="mb-3">
              Password reset email sent!
            </v-alert>
            <p class="text-body-2">
              Check your inbox at <strong>{{ resetEmail }}</strong> for the reset link.
            </p>
          </template>
        </v-card-text>

        <v-card-actions class="pa-4 pt-0">
          <v-btn
            variant="text"
            size="small"
            @click="closeForgotPasswordModal"
            :disabled="resetLoading"
          >
            {{ resetEmailSent ? 'Close' : 'Cancel' }}
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            v-if="!resetEmailSent"
            color="primary"
            size="small"
            elevation="0"
            :loading="resetLoading"
            :disabled="!resetFormValid || resetLoading"
            @click="handleSendResetEmail"
          >
            Send Reset Link
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script setup>
import { ref, computed, watch, onUnmounted } from 'vue'
import apiService from '../services/api'
import authService from '../services/auth'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:modelValue', 'authenticated', 'cancel'])

const showModal = ref(props.modelValue)
const currentStep = ref('auth') // 'auth' or 'verify'
const currentTab = ref('signup') // Default to signup for new users
const loading = ref(false)
const error = ref('')
const canRetryError = ref(false)
const lastFailedAction = ref(null)
const showSignupResetHint = ref(false)

// Sign In fields
const signinEmail = ref('')
const signinPassword = ref('')
const showSigninPassword = ref(false)
const signinValid = ref(false)
const signinForm = ref(null)

// Sign Up fields
const signupName = ref('')
const signupEmail = ref('')
const signupPassword = ref('')
const signupPasswordConfirm = ref('')
const showSignupPassword = ref(false)
const showSignupPasswordConfirm = ref(false)
const signupValid = ref(false)
const signupForm = ref(null)

// Verification fields
const verificationCode = ref('')
const verifyValid = ref(false)
const verifyForm = ref(null)
const signupToken = ref('')
const resending = ref(false)
const resendCooldown = ref(0)
let resendTimer = null

// Forgot password state
const showForgotPassword = ref(false)
const resetEmail = ref('')
const resetFormValid = ref(false)
const resetLoading = ref(false)
const resetError = ref('')
const resetEmailSent = ref(false)
const resetForm = ref(null)

// Validation rules
const nameRules = [
  v => !!v || 'Name is required'
]

const emailRules = [
  v => !!v || 'Email is required',
  v => /.+@.+\..+/.test(v) || 'Email must be valid'
]

const passwordRules = [
  v => !!v || 'Password is required',
  v => (v && v.length >= 6) || 'Password must be at least 6 characters'
]

const confirmPasswordRules = [
  v => !!v || 'Please confirm your password',
  v => v === signupPassword.value || 'Passwords do not match'
]

const codeRules = [
  v => !!v || 'Verification code is required',
  v => v.length === 5 || 'Code must be 5 characters'
]

const isFormValid = computed(() => {
  if (currentTab.value === 'signin') {
    return signinValid.value
  } else {
    return signupValid.value
  }
})

const getCurrentStepIcon = computed(() => {
  if (currentStep.value === 'verify') {
    return 'mdi-shield-check-outline'
  }
  return 'mdi-content-save-outline'
})

const getCurrentStepTitle = computed(() => {
  if (currentStep.value === 'verify') {
    return 'Verify Email'
  }
  return 'Save Your Memorial'
})

// Watch for prop changes
watch(() => props.modelValue, (newValue) => {
  showModal.value = newValue
  if (newValue) {
    // Reset form when modal opens
    resetModalState()
  }
})

// Watch for modal changes
watch(showModal, (newValue) => {
  emit('update:modelValue', newValue)
})

const resetModalState = () => {
  currentStep.value = 'auth'
  currentTab.value = 'signup'
  error.value = ''
  canRetryError.value = false
  lastFailedAction.value = null
  verificationCode.value = ''
  signupToken.value = ''
  clearResendTimer()
}

const handleCancel = () => {
  showModal.value = false
  emit('cancel')
}

const handleSubmit = () => {
  if (currentTab.value === 'signin') {
    handleSignIn()
  } else {
    handleSignUp()
  }
}

const handleSignIn = async () => {
  if (!signinValid.value) return

  loading.value = true
  error.value = ''

  try {
    const result = await authService.login(signinEmail.value, signinPassword.value)

    if (result.success) {
      loading.value = false
      showModal.value = false
      emit('authenticated', result.response)
    } else {
      error.value = result.error || 'Sign in failed. Please check your credentials.'
    }
  } catch (err) {
    console.error('Sign in error:', err)
    error.value = 'An error occurred during sign in. Please try again.'
  } finally {
    loading.value = false
  }
}

const handleSignUp = async () => {
  if (!signupValid.value) return

  loading.value = true
  error.value = ''
  showSignupResetHint.value = false
  canRetryError.value = false
  lastFailedAction.value = null

  try {
    console.log('Creating account for:', signupEmail.value)
    const response = await apiService.signUp(signupEmail.value, signupPassword.value, null, signupName.value)
    console.log('Account creation response:', response)

    if (response.success && response.token) {
      // Store the signup token for verification
      signupToken.value = response.token
      apiService.setToken(response.token)

      loading.value = false
      currentStep.value = 'verify'
      startResendCooldown()
    } else if (response.pendingVerification && response.token) {
      // Account exists but is not verified
      signupToken.value = response.token
      apiService.setToken(response.token)

      loading.value = false
      currentStep.value = 'verify'
      startResendCooldown()
    } else {
      error.value = response.msg || 'Sign up failed. Please try again.'
      showSignupResetHint.value = /password|already|exists|registered|in use/i.test(error.value)
      canRetryError.value = true
      lastFailedAction.value = 'signup'
    }
  } catch (err) {
    console.error('Sign up error:', err)
    const errorInfo = apiService.formatErrorMessage(err)
    error.value = errorInfo.message
    showSignupResetHint.value = /password|already|exists|registered|in use/i.test(error.value)
    canRetryError.value = errorInfo.canRetry
    lastFailedAction.value = 'signup'
  } finally {
    loading.value = false
  }
}

const verifyCode = async () => {
  if (!verifyValid.value) return

  loading.value = true
  error.value = ''
  canRetryError.value = false
  lastFailedAction.value = null

  try {
    const response = await apiService.confirmCode(verificationCode.value, signupToken.value)

    if (response.success) {
      loading.value = false

      // Emit authenticated event with token and user info
      const authResponse = {
        token: signupToken.value,
        user: response.user || {
          email: signupEmail.value,
          name: signupName.value
        }
      }

      showModal.value = false
      emit('authenticated', authResponse)
    } else {
      throw new Error(response.msg || 'Verification failed')
    }
  } catch (err) {
    loading.value = false
    const errorInfo = apiService.formatErrorMessage(err)
    error.value = errorInfo.message
    canRetryError.value = errorInfo.canRetry
    lastFailedAction.value = 'verify'
    console.error('Verification error:', err)
  }
}

const resendCode = async () => {
  if (resendCooldown.value > 0 || resending.value) return

  resending.value = true
  error.value = ''
  canRetryError.value = false

  try {
    const response = await apiService.resendCode(signupToken.value)

    if (response.success) {
      resending.value = false
      startResendCooldown()
    } else {
      throw new Error(response.msg || 'Failed to resend code')
    }
  } catch (err) {
    resending.value = false
    const errorInfo = apiService.formatErrorMessage(err)
    error.value = errorInfo.message
    canRetryError.value = errorInfo.canRetry
    lastFailedAction.value = 'resend'
    console.error('Resend code error:', err)
  }
}

const goBackToSignup = () => {
  currentStep.value = 'auth'
  verificationCode.value = ''
  error.value = ''
  canRetryError.value = false
  lastFailedAction.value = null
  clearResendTimer()
}

const retryLastAction = async () => {
  if (!lastFailedAction.value) return

  error.value = ''
  canRetryError.value = false

  switch (lastFailedAction.value) {
    case 'signup':
      await handleSignUp()
      break
    case 'verify':
      await verifyCode()
      break
    case 'resend':
      await resendCode()
      break
    default:
      console.warn('Unknown action to retry:', lastFailedAction.value)
  }
}

const startResendCooldown = () => {
  resendCooldown.value = 30
  resendTimer = setInterval(() => {
    resendCooldown.value--
    if (resendCooldown.value <= 0) {
      clearResendTimer()
    }
  }, 1000)
}

const clearResendTimer = () => {
  if (resendTimer) {
    clearInterval(resendTimer)
    resendTimer = null
  }
  resendCooldown.value = 0
}

// Forgot password methods
const handleSendResetEmail = async () => {
  if (!resetFormValid.value) return

  resetLoading.value = true
  resetError.value = ''

  try {
    const response = await apiService.sendPasswordResetEmail(resetEmail.value)

    if (response.success) {
      resetEmailSent.value = true
    } else {
      resetError.value = response.msg || 'Failed to send reset email. Please try again.'
    }
  } catch (err) {
    console.error('Password reset error:', err)
    const errorInfo = apiService.formatErrorMessage(err)
    resetError.value = errorInfo.message
  } finally {
    resetLoading.value = false
  }
}

const closeForgotPasswordModal = () => {
  showForgotPassword.value = false
  // Reset the modal state after a short delay
  setTimeout(() => {
    resetEmail.value = ''
    resetError.value = ''
    resetEmailSent.value = false
  }, 300)
}

onUnmounted(() => {
  clearResendTimer()
})
</script>

<style lang="scss" scoped>
@use "sass:color";
@use "~@/styles/variables" as *;
@use "~@/styles/mixins" as *;

.save-auth-modal {
  border-radius: $border-radius-lg;
  overflow: hidden;

  .modal-header {
    background: linear-gradient(135deg, rgba($primary, 0.1) 0%, rgba($primary, 0.05) 100%);
    border-bottom: $border-width-thin solid rgba($primary, 0.2);

    .v-card-title {
      font-weight: 600;
      color: $text-primary;
    }
  }

  .modal-content {
    .text-body-1 {
      color: $text-primary;
      line-height: 1.6;
    }
  }

  .verification-actions {
    display: flex;
    justify-content: flex-start;
    align-items: center;
  }

  .v-card-actions {
    .v-btn {
      border-radius: $border-radius;
      font-weight: 500;
      text-transform: none;
      letter-spacing: 0.025em;
    }
  }
}

.auth-tabs {
  border: $border-width solid rgba(15, 23, 42, 0.12);
  border-radius: 8px;
  overflow: hidden;
  background: var(--v-theme-surface);

  :deep(.v-tab) {
    border-radius: 0;
    border: none !important;
    min-height: 40px;
    text-transform: none;
  }

  :deep(.v-tab:not(:last-child)) {
    border-right: $border-width solid rgba(15, 23, 42, 0.12) !important;
  }
}

.name-note {
  margin-top: -15px;
}

@include mobile {
  .save-auth-modal {
    margin: 1rem;

    .pa-6 {
      padding: 1.5rem !important;
    }

    .verification-actions {
      flex-direction: column;
      align-items: flex-start;
      gap: 8px;

      .ml-4 {
        margin-left: 0 !important;
      }
    }

    .v-card-actions {
      flex-direction: column;
      gap: 12px;

      .v-btn {
        width: 100%;

        &:first-child {
          order: 2;
        }

        &:last-child {
          order: 1;
        }
      }

      .v-spacer {
        display: none;
      }
    }
  }
}
</style>
